import { metaFor } from '../data/categories';
import { productPills } from '../data/products';
import { productsCalloutHtml } from './callouts';
import { withPlayerName } from '../player-name';
import { sparklinePath } from '../sparkline';
import { isNew, isTrending, isUpdated } from '../trending';
import type { Entry, SortKey } from '../types';
import { expansionHtml } from './detail';
import { iconSvg } from './icon';
import { countryName, escapeHtml, formatCount, formatDate, prefersReducedMotion } from './util';

export type ViewMode = 'rows' | 'cards';

export interface ListCallbacks {
  onDetail: (slug: string) => void;
  onCountry: (iso: string) => void;
  onSort: (sort: SortKey) => void;
}

export interface ListOptions {
  /** Analytics exist at all — drives the Trend + Visits columns. */
  hasAnalytics: boolean;
  /** Weekly data exists — only the trending/popular sorts need it. The Trend
   *  column itself does not: a quiet week still has real traffic to draw. */
  hasWeekly: boolean;
  sort: SortKey;
  /** Tooltip shown on analytics-sort headers while they're disabled. */
  warmingNote: string | null;
  /** Current time (ms) for the 24h NEW and UPDATED windows. */
  nowMs: number;
  expandedSlug: string | null;
  view: ViewMode;
  /** The list is filtered to Ben's products: it is headed by a callout that
   *  they aren't part of the experiment. */
  productsView?: boolean;
}

/** Every entry with a series gets a line — a quiet entry draws a flat baseline
 *  rather than an empty cell, so the column reads as real traffic throughout. */
function sparkHtml(entry: Entry): string {
  if (entry.product) return ''; // a product's analytics are never shown
  const series = entry.stats?.agg.spark14 ?? [];
  if (series.length < 2) return '';
  return `<svg class="spark" viewBox="0 0 64 16" aria-hidden="true">
    <path d="${sparklinePath(series, 64, 16, 1)}" fill="none" />
  </svg>`;
}

function visitsHtml(entry: Entry): string {
  if (entry.product) return ''; // a product's analytics are never shown
  const agg = entry.stats?.agg;
  if (!agg) return '';
  const up = isTrending(agg) ? '<span class="mark-up" title="Trending">▲</span> ' : '';
  if (agg.v7 > 0) {
    return `${up}<span title="Unique visits, last 7 days">${formatCount(agg.v7)}</span>`;
  }
  if (agg.vAll > 0) {
    return `<span title="Unique visits, all time (weekly counts appear after the first full week)">${formatCount(agg.vAll)}</span>`;
  }
  return '';
}

function launchHtml(entry: Entry): string {
  if (!entry.url) return '<span class="cell-launch"></span>';
  // Carry the visitor's game name onward so a game they have not played before
  // does not ask them to type it again. See src/player-name.ts.
  return `<a class="cell-launch" href="${escapeHtml(withPlayerName(entry.url, entry.type))}" target="_blank" rel="noopener"
    title="Open ${escapeHtml(entry.name)}" aria-label="Open ${escapeHtml(entry.name)}">${iconSvg('arrow-up-right', 14)}</a>`;
}

function nameHtml(entry: Entry, opts: ListOptions): string {
  // NEW takes precedence over UPDATED — a brand-new entry never also reads as updated.
  // A product is never NEW: one shipped is dated the day it shipped, and one
  // coming soon hasn't shipped. Its expanded row says what it is.
  const mark = entry.product
    ? ''
    : isNew(entry, opts.nowMs)
    ? ' <sup class="mark-new">NEW</sup>'
    : isUpdated(entry, opts.nowMs)
      ? ' <sup class="mark-updated">UPDATED</sup>'
      : '';
  if (entry.product) {
    return `<span class="cell-name cell-name--product"><span class="name-text">${escapeHtml(entry.name)}</span>${pillsHtml(entry)}</span>`;
  }
  return `<span class="cell-name"><span class="name-text">${escapeHtml(entry.name)}</span>${mark}</span>`;
}

/** The ship date, or "Coming soon" for a product that hasn't shipped. */
function shippedHtml(entry: Entry): string {
  if (entry.product?.comingSoon) return '<span class="mark-soon">Coming soon</span>';
  return escapeHtml(formatDate(entry.date));
}

/** A product's price model and platforms as small pills on its name's line. */
export function pillsHtml(entry: Entry): string {
  const pills = productPills(entry);
  if (!pills) return '';
  return `<span class="product-pills">${[pills.price, ...pills.platforms]
    .map((w) => `<span class="pill">${escapeHtml(w)}</span>`)
    .join('')}</span>`;
}

function descHtml(entry: Entry): string {
  return escapeHtml(entry.description);
}

function flagHtml(entry: Entry): string {
  if (!entry.country) return '';
  return `<button class="cell-flag" data-flag="${escapeHtml(entry.country)}"
    title="Filter: ${escapeHtml(countryName(entry.country))}" aria-label="Filter by ${escapeHtml(countryName(entry.country))}">${escapeHtml(entry.country)}</button>`;
}

function rowHtml(entry: Entry, opts: ListOptions): string {
  const meta = metaFor(entry.category);
  const expanded = opts.expandedSlug === entry.slug;
  const analyticsCells = opts.hasAnalytics
    ? `<span class="cell-spark">${sparkHtml(entry)}</span>
       <span class="cell-visits">${visitsHtml(entry)}</span>`
    : '';
  const row = `
    <div class="row${expanded ? ' row-open' : ''}" data-slug="${escapeHtml(entry.slug)}" tabindex="0" role="button"
      aria-expanded="${expanded}" aria-label="Details for ${escapeHtml(entry.name)}">
      <span class="cell-icon" data-icon-burst>${iconSvg(entry.icon, 18)}</span>
      ${nameHtml(entry, opts)}
      <span class="cell-country">${flagHtml(entry)}</span>
      <span class="cell-desc">${descHtml(entry)}</span>
      <span class="cell-category">${escapeHtml(meta.label)}</span>
      <span class="cell-date">${shippedHtml(entry)}</span>
      ${analyticsCells}
      ${launchHtml(entry)}
      <span class="cell-toggle" data-detail aria-hidden="true">${iconSvg(expanded ? 'chevron-up' : 'chevron-down', 14)}</span>
    </div>`;
  return row + (expanded ? expansionHtml(entry, opts) : '');
}

function cardHtml(entry: Entry, opts: ListOptions): string {
  const meta = metaFor(entry.category);
  const expanded = opts.expandedSlug === entry.slug;
  const visits = opts.hasAnalytics ? visitsHtml(entry) : '';
  return `
    <div class="card${expanded ? ' card-open' : ''}" data-slug="${escapeHtml(entry.slug)}" tabindex="0" role="button"
      aria-expanded="${expanded}" aria-label="Details for ${escapeHtml(entry.name)}">
      <div class="card-top">
        <span class="cell-icon" data-icon-burst>${iconSvg(entry.icon, 20)}</span>
        ${launchHtml(entry)}
      </div>
      ${nameHtml(entry, opts)}
      <p class="card-desc">${descHtml(entry)}</p>
      <div class="card-foot">
        <span class="card-meta">${flagHtml(entry)}${entry.country ? ' · ' : ''}${escapeHtml(meta.label)} · ${shippedHtml(entry)}</span>
        <span class="cell-visits">${visits}</span>
      </div>
    </div>` + (expanded ? expansionHtml(entry, opts) : '');
}

/** A sortable column header. Fixed sort directions: ↓ newest/popular/trending, ↑ A–Z. */
function capSort(opts: ListOptions, cls: string, label: string, key: SortKey, enabled: boolean): string {
  if (!enabled) {
    return `<span class="${cls} cap-disabled" title="${escapeHtml(opts.warmingNote ?? '')}">${label}</span>`;
  }
  const active = opts.sort === key;
  const dir = active ? `<span class="cap-dir">${key === 'az' ? '↑' : '↓'}</span>` : '';
  return `<button class="${cls} cap-sort${active ? ' cap-active' : ''}" data-sortkey="${key}"
    aria-pressed="${active}" title="Sort by ${label.toLowerCase()}">${label}${dir}</button>`;
}

function captionsHtml(opts: ListOptions): string {
  // The Trend/Visits captions must match rowHtml's cell count exactly — both key
  // off hasAnalytics — or the shared grid template shifts every later column.
  const tail = opts.hasAnalytics
    ? `${capSort(opts, 'cell-date', 'Shipped', 'new', true)}
       ${capSort(opts, 'cell-spark', 'Trend', 'trending', opts.hasWeekly)}
       ${capSort(opts, 'cell-visits', 'Visits', 'popular', opts.hasWeekly)}`
    : capSort(opts, 'cell-date', 'Shipped', 'new', true);
  return `
    <div class="list-captions">
      <span class="cell-icon"></span>
      ${capSort(opts, 'cell-name', 'Name', 'az', true)}
      <span class="cell-country">Country</span>
      <span class="cell-desc"></span>
      <span class="cell-category">Category</span>
      ${tail}
      <span class="cell-launch"></span>
      <span class="cell-toggle"></span>
    </div>`;
}

export function renderList(
  container: HTMLElement,
  entries: Entry[],
  opts: ListOptions,
  cb: ListCallbacks,
): void {
  container.classList.toggle('list--noanalytics', !opts.hasAnalytics);
  container.classList.toggle('list--products', !!opts.productsView);
  container.classList.toggle('list--cards', opts.view === 'cards');
  const note = opts.productsView ? `<div class="list-note">${productsCalloutHtml()}</div>` : '';
  if (opts.view === 'cards') {
    container.innerHTML = note + entries.map((e) => cardHtml(e, opts)).join('');
  } else {
    container.innerHTML = note + captionsHtml(opts) + entries.map((e) => rowHtml(e, opts)).join('');
  }

  container.querySelectorAll<HTMLButtonElement>('[data-sortkey]').forEach((btn) => {
    btn.addEventListener('click', () => cb.onSort(btn.dataset.sortkey as SortKey));
  });

  container.querySelectorAll<HTMLButtonElement>('[data-flag]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      cb.onCountry(btn.dataset.flag!);
    });
  });

  // Clicking an item toggles its expansion; anchors (launch, expansion links),
  // flags, and icon-bursts keep their own behavior.
  container.querySelectorAll<HTMLElement>('[role="button"][data-slug]').forEach((item) => {
    const toggle = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest('a, [data-flag], [data-icon-burst]')) return;
      cb.onDetail(item.dataset.slug!);
    };
    item.addEventListener('click', toggle);
    item.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle(e);
      }
    });
  });
}

/** Wire the burst on item icons (delegated once; survives re-renders). */
export function bindIconBurst(
  container: HTMLElement,
  onBurst: (slug: string, x: number, y: number) => void,
): void {
  container.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest('[data-icon-burst]');
    if (!target) return;
    e.preventDefault();
    e.stopPropagation();
    const slug = (target.closest('[data-slug]') as HTMLElement | null)?.dataset.slug;
    const rect = target.getBoundingClientRect();
    if (slug) onBurst(slug, rect.left + rect.width / 2, rect.top + rect.height / 2);
  });
}

/** Roulette: a single highlight sweeps row-to-row / tile-to-tile and settles on
 *  the winner. The path steps one item at a time (bouncing off the list ends) so
 *  the spotlight always travels to an adjacent item — it glides rather than jumps —
 *  and decelerates onto the winner. */
export function rouletteHighlight(container: HTMLElement, winnerSlug: string): Promise<void> {
  return new Promise((resolve) => {
    const items = [...container.querySelectorAll<HTMLElement>('[data-slug]')];
    const winnerIdx = items.findIndex((r) => r.dataset.slug === winnerSlug);
    if (winnerIdx < 0 || prefersReducedMotion()) {
      resolve();
      return;
    }

    const spot = document.createElement('div');
    spot.className = 'roulette-spotlight';
    spot.setAttribute('aria-hidden', 'true');
    container.appendChild(spot);

    const place = (el: HTMLElement) => {
      spot.style.width = `${el.offsetWidth}px`;
      spot.style.height = `${el.offsetHeight}px`;
      spot.style.transform = `translate(${el.offsetLeft}px, ${el.offsetTop}px)`;
    };
    // Keep the page centred on whichever item the spotlight is on, so on a phone
    // the viewport travels up and down *with* the sweep instead of jumping
    // straight to the winner. A dedicated rAF loop eases scrollY toward the
    // current target every frame — smoother and more reliable than firing
    // scrollIntoView({behavior:'smooth'}) repeatedly, which browsers throttle.
    const centerYOf = (el: HTMLElement): number => {
      const rect = el.getBoundingClientRect();
      const y = window.scrollY + rect.top + rect.height / 2 - window.innerHeight / 2;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      return Math.max(0, Math.min(max, y));
    };
    let targetY = window.scrollY;
    let following = true;
    let done = false;
    const cleanup = () => {
      if (done) return; // idempotent — may be reached from the follow guard or the end
      done = true;
      following = false;
      spot.remove();
      resolve();
    };
    const followFrame = () => {
      if (!following) return;
      // The list can re-render mid-animation (repo-pushes / investigations land
      // after first paint). If our spotlight was torn out, stop chasing stale
      // elements and end cleanly rather than scrolling against detached nodes.
      if (!spot.isConnected) {
        cleanup();
        return;
      }
      const cur = window.scrollY;
      const delta = targetY - cur;
      window.scrollTo(0, Math.abs(delta) < 0.5 ? targetY : cur + delta * 0.18);
      requestAnimationFrame(followFrame);
    };
    requestAnimationFrame(followFrame);

    // A single item can't sweep — flash it and go.
    const n = items.length;
    if (n < 2) {
      place(items[winnerIdx]);
      targetY = centerYOf(items[winnerIdx]);
      requestAnimationFrame(() => (spot.style.opacity = '1'));
      setTimeout(() => {
        spot.style.opacity = '0';
        setTimeout(cleanup, 220);
      }, 520);
      return;
    }

    // Build a walk that ends on the winner: step outward from it (bouncing at the
    // ends), then reverse so the sequence approaches the winner one item at a time.
    const steps = Math.min(16, Math.max(8, n));
    const path = [winnerIdx];
    let i = winnerIdx;
    let dir = winnerIdx === 0 ? 1 : -1;
    for (let s = 0; s < steps; s++) {
      if (i + dir < 0 || i + dir >= n) dir = -dir;
      i += dir;
      path.push(i);
    }
    path.reverse();

    place(items[path[0]]);
    targetY = centerYOf(items[path[0]]);
    requestAnimationFrame(() => (spot.style.opacity = '1'));

    let step = 1;
    const run = () => {
      if (done || !spot.isConnected) {
        cleanup();
        return;
      }
      const t = step / (path.length - 1); // 0 → 1 across the sweep
      const dur = Math.round(55 + t * t * 150); // ~55ms early, easing out to ~205ms
      spot.style.transition =
        `transform ${dur}ms cubic-bezier(0.33, 0, 0.2, 1), width ${dur}ms ease, height ${dur}ms ease, opacity 160ms ease`;
      place(items[path[step]]);
      targetY = centerYOf(items[path[step]]);
      step += 1;
      if (step < path.length) {
        setTimeout(run, dur);
      } else {
        spot.classList.add('landed');
        setTimeout(() => {
          spot.style.opacity = '0';
          setTimeout(cleanup, 220);
        }, 380);
      }
    };
    setTimeout(run, 70);
  });
}
